// ============================================================
// TMR Portal Admin — XLSX, com a biblioteca carregada só no clique
// ============================================================
// Type-only: erased at compile time, so it does not pull the writer into the main bundle. The value is
// loaded by the dynamic `import()` inside `toXlsxBlob`.
import type { Row, SheetData } from 'write-excel-file/browser';
import type { ExportColumn, ExportValue } from './types';

/**
 * Narrowest column, so a header like "UF" still reads, and widest, so one long description does not push
 * every other column off the screen. Widths are in characters, which is what the format uses.
 */
const MIN_WIDTH = 10;
const MAX_WIDTH = 48;

/** One cell as the writer wants it: a value plus the constructor that says how to type it. */
function toCell(value: ExportValue): Row[number] {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? null
      : { type: Date, value, format: 'dd/mm/yyyy hh:mm' };
  }
  if (typeof value === 'number') return { type: Number, value };
  if (typeof value === 'boolean') return { type: String, value: value ? 'Sim' : 'Não' };
  return { type: String, value };
}

/** A column wide enough for its header and its longest value, within reason. */
function widthOf(header: string, cells: readonly string[]): number {
  const longest = cells.reduce((max, text) => Math.max(max, text.length), header.length);
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, longest + 2));
}

/** How wide a value reads, for sizing a column. */
function widthText(value: ExportValue): string {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return '00/00/0000 00:00';
  return String(value);
}

/**
 * Writes rows into an XLSX blob.
 *
 * **The library is loaded by dynamic `import()`, on purpose.** It is the only third-party dependency this
 * feature adds, and the overwhelming majority of page loads never export anything — so whoever does not
 * click "Exportar" does not pay for it. The CSV path has no dependency at all.
 *
 * @param rows The rows to write, already filtered and ordered as the screen had them.
 * @param columns Header and reader for each column.
 */
export async function toXlsxBlob<T>(
  rows: readonly T[],
  columns: readonly ExportColumn<T>[],
): Promise<Blob> {
  const { default: writeXlsxFile } = await import('write-excel-file/browser');

  const values = rows.map((row) => columns.map((column) => column.value(row)));

  // The header is a row like any other, only bold — this is a table people read, not a report.
  const header: Row = columns.map((column) => ({ value: column.header, fontWeight: 'bold' }));
  const sheet: SheetData = [header, ...values.map((row) => row.map(toCell))];

  const widths = columns.map((column, index) =>
    widthOf(column.header, values.map((row) => widthText(row[index]))),
  );

  return writeXlsxFile(sheet, {
    columns: widths.map((width) => ({ width })),
    // The row the reader keeps in view while scrolling a few thousand rows.
    stickyRowsCount: 1,
  }).toBlob();
}
